import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSkipSummary,
  fetchSkipFilings,
  fetchFiling,
  toggleLiverun,
} from "../reducers/skipAnalysisSlice";
import LabFilingDetails from "./LabFilingDetails";

const CompactHeader = ({ children }) => (
  <Box
    sx={{
      textAlign: "center",
      lineHeight: 1.5,
      fontSize: "0.72rem",
      fontWeight: 500,
      whiteSpace: "normal",
    }}
  >
    {children}
  </Box>
);

function CountFooter({ shown, total, noun }) {
  return (
    <GridFooterContainer
      sx={{ px: 1.5, py: 0.5, minHeight: 32, justifyContent: "flex-start" }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {(() => {
          const plural =
            total === 1
              ? noun
              : /[^aeiou]y$/i.test(noun)
                ? `${noun.slice(0, -1)}ies`
                : `${noun}s`;

          return shown === total
            ? `${total} ${plural}`
            : `Showing ${shown} of ${total} ${plural}`;
        })()}
      </Typography>
    </GridFooterContainer>
  );
}

const TABLE_DAYDATA_COLUMNS = [
  {
    field: "nhs_number",
    flex: 0.5,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    renderHeader: () => <CompactHeader>NHS No</CompactHeader>,
  },
  {
    field: "lab_report_id",
    flex: 1.5,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
    headerName: "Lab Report Id",
  },
  {
    field: "category",
    headerName: "Category",
    flex: 2,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "category_type",
    flex: 1,
    align: "center",
    headerAlign: "center",
    headerName: "Report Type",

    cellClassName: "bg-grey",
    renderCell: (params) =>
      params.value ? "Normal Report" : "Abnormal Report",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "starttime",
    flex: 0.5,
    align: "center",
    headerName: "Start Time",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "notes",
    headerName: "Notes",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
  {
    field: "msg",
    headerName: "Reason",
    flex: 3,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-grey",
  },
];

const SUMMARY_COLUMNS = [
  {
    field: "category_names",
    headerName: "Category",
    flex: 2,
    cellClassName: "bg-grey",
    valueGetter: (params) =>
      Array.isArray(params)
        ? params.join(", ")
        : typeof params === "string"
          ? params
          : "—",
  },
  {
    field: "subtest_names",
    headerName: "Subtests",
    flex: 2,
    cellClassName: "bg-grey",
    valueGetter: (params) =>
      Array.isArray(params)
        ? params.join(", ")
        : typeof params === "string"
          ? params
          : "—",
  },
  {
    field: "skip_type",
    flex: 2,
    cellClassName: "bg-grey",
    headerName: "Skip Type",
  },
  {
    field: "count",
    headerName: "Count",
    flex: 1,
    cellClassName: "bg-grey",
  },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (d) =>
  new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

const gridRowSx = {
  height: "100%",
  border: "none",
  "& .MuiDataGrid-columnHeaders": { marginBottom: "0px" },
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .selected-day-row": {
    backgroundColor: "#fef9c3 !important",
    outline: "2px solid #7c6fe0",
    outlineOffset: "-2px",
    fontWeight: 700,
  },
  "& .selected-day-row:hover": {
    backgroundColor: "#fef08a !important",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
    {
      outline: "none",
    },
};

const SkipFrame = () => {
  const dispatch = useDispatch();
  const {
    summary,
    filings,
    lab,
    liverun,
    loadingSummary,
    loadingFilings,
    loadingLabReport,
  } = useSelector((s) => s.skipAnalysis);

  const license_key = useSelector((s) => s.authen.loggedInUser?.license_key);

  const access_token = useSelector((state) => state.authen.accessToken);
  const is_admin = Boolean(access_token);

  const [openFilingDialog, setOpenFilingDialog] = useState(false);
  const [selectedNHSId, setSelectedNHSId] = useState(null);
  const [selectedCategoryRowId, setSelectedCategoryRowId] = useState(null);

  const [startDate, setStartDate] = useState(daysAgoISO(60));
  const [endDate, setEndDate] = useState(todayISO());

  const [categoryFilter, setCategoryFilter] = useState([]);
  const [nhsFilter, setNhsFilter] = useState([]);

  useEffect(() => {
    if (license_key) {
      dispatch(
        fetchSkipSummary({
          start_date: startDate,
          end_date: endDate,
          liverun,
          license_key,
        }),
      );
    }
  }, [startDate, endDate, liverun, license_key, dispatch]);

  useEffect(() => {
    setCategoryFilter([]);
    setNhsFilter([]);
    setSelectedCategoryRowId(null);
    setSelectedNHSId(null);
  }, [startDate, endDate]);

  const categoryOptions = React.useMemo(() => {
    const set = new Set();
    (summary || []).forEach((r) => {
      const names = Array.isArray(r.category_names)
        ? r.category_names
        : [r.category_names];
      names.filter(Boolean).forEach((c) => set.add(c));
    });
    return Array.from(set)
      .sort()
      .map((cat) => ({ label: cat, value: cat }));
  }, [summary]);

  const nhsNumberOptions = React.useMemo(() => {
    const set = new Set(
      (filings || []).map((r) => r.nhs_number).filter(Boolean),
    );
    return Array.from(set)
      .sort()
      .map((nhs) => ({ label: String(nhs), value: nhs }));
  }, [filings]);

  const filteredSummary = React.useMemo(() => {
    if (categoryFilter.length === 0) return summary;
    return (summary || []).filter((row) => {
      const names = Array.isArray(row.category_names)
        ? row.category_names
        : [row.category_names];
      return names.some((c) => categoryFilter.includes(c));
    });
  }, [summary, categoryFilter]);

  const filteredFilings = React.useMemo(() => {
    if (nhsFilter.length === 0) return filings;
    return (filings || []).filter((row) => nhsFilter.includes(row.nhs_number));
  }, [filings, nhsFilter]);

  useEffect(() => {
    if (categoryFilter.length > 0 && filteredSummary.length === 1) {
      const row = filteredSummary[0];
      if (row.id !== selectedCategoryRowId) {
        setSelectedCategoryRowId(row.id);
        setNhsFilter([]);
        setSelectedNHSId(null);
        dispatch(
          fetchSkipFilings({
            start_date: startDate,
            end_date: endDate,
            category_names: row.category_names,
            subtest_names: row.subtest_names,
            skip_type: row.skip_type,
            liverun,
            license_key,
          }),
        );
      }
    }
  }, [filteredSummary, categoryFilter, liverun]);

  const handleSummaryRowClick = (params) => {
    setSelectedCategoryRowId(params.row?.id);
    setNhsFilter([]);
    setSelectedNHSId(null);

    dispatch(
      fetchSkipFilings({
        start_date: startDate,
        end_date: endDate,
        category_names: params.row.category_names,
        subtest_names: params.row.subtest_names,
        skip_type: params.row.skip_type,
        liverun,
        license_key,
      }),
    );
  };

  const handleOpenFiling = (params) => {
    const lab_report_id = params.row?.lab_report_id;

    dispatch(fetchFiling({ lab_report_id, liverun, license_key }));
    setOpenFilingDialog(true);
  };

  const handleCloseFilingDialog = () => {
    setOpenFilingDialog(false);
  };

  const handleRowClick = (params) => {
    setSelectedNHSId(params.row?.id);
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 50px)",
        display: "flex",
        flexDirection: "column",
        ml: -1,
        mr: -1,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          mb: 0.5,
          mt: 0.5,
          borderRadius: 2,
          borderColor: "#cbd5e0",
          background: "#fafafa",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.75 }}>
            Skip Analysis
          </Typography>
          <TextField
            type="date"
            label="Start Date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { fontSize: "0.8rem" },
              "& .MuiInputLabel-root": {
                backgroundColor: "#fff",
                px: 0.5,
              },
            }}
          />
          <TextField
            type="date"
            label="End Date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { fontSize: "0.8rem" },
              "& .MuiInputLabel-root": {
                backgroundColor: "#fff",
                px: 0.5,
              },
            }}
          />

          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 260 }}
            options={categoryOptions}
            getOptionLabel={(option) => option.label || ""}
            filterOptions={(options, state) => {
              const input = (state.inputValue || "").toLowerCase();
              return options.filter((option) =>
                (option.label || "").toLowerCase().includes(input),
              );
            }}
            value={categoryOptions.filter((opt) =>
              categoryFilter.includes(opt.value),
            )}
            onChange={(event, newValue) => {
              setCategoryFilter(newValue.map((v) => v.value));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Path Category Name"
                size="small"
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.8rem" },
                  "& .MuiInputLabel-root": {
                    backgroundColor: "#fff",
                    px: 0.5,
                  },
                }}
              />
            )}
          />

          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 220 }}
            options={nhsNumberOptions}
            getOptionLabel={(option) => option.label || ""}
            filterOptions={(options, state) => {
              const input = (state.inputValue || "").toLowerCase();
              return options.filter((option) =>
                (option.label || "").toLowerCase().includes(input),
              );
            }}
            value={nhsNumberOptions.filter((opt) =>
              nhsFilter.includes(opt.value),
            )}
            onChange={(event, newValue) => {
              setNhsFilter(newValue.map((v) => v.value));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="NHS Number"
                size="small"
                inputProps={{
                  ...params.inputProps,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  onKeyDown: (e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      ![
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": { fontSize: "0.8rem" },
                  "& .MuiInputLabel-root": {
                    backgroundColor: "#fff",
                    px: 0.5,
                  },
                }}
              />
            )}
          />
          {is_admin && (
            <FormControlLabel
              sx={{ m: 0, ml: "auto" }}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={liverun}
                  onChange={() => dispatch(toggleLiverun())}
                  color="success"
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: liverun ? "#2f855a" : "#c53030",
                  }}
                >
                  {liverun ? "Live Run" : "Dry Run"}
                </Typography>
              }
            />
          )}
        </Stack>
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            p: 1,
            overflow: "hidden",
            borderRadius: 2,
            borderColor: "#cbd5e0",
          }}
        >
          {loadingSummary ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <DataGrid
                variant="dense"
                rowHeight={26}
                rows={filteredSummary}
                columns={SUMMARY_COLUMNS}
                onRowClick={handleSummaryRowClick}
                hideFooterPagination
                hideFooterSelectedRowCount
                getRowClassName={(params) => {
                  const isSelectedRow = params.row.id === selectedCategoryRowId;
                  if (isSelectedRow) return "selected-day-row";
                  return params.indexRelativeToCurrentPage % 2 === 0
                    ? "even"
                    : "odd";
                }}
                slots={{
                  footer: () => (
                    <CountFooter
                      shown={filteredSummary.length}
                      total={summary.length}
                      noun="category"
                    />
                  ),
                }}
                sx={gridRowSx}
              />
            </Box>
          )}
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            p: 1,
            overflow: "hidden",
            borderRadius: 2,
            borderColor: "#cbd5e0",
          }}
        >
          {loadingFilings ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <DataGrid
                variant="dense"
                rowHeight={26}
                rows={filteredFilings}
                columns={TABLE_DAYDATA_COLUMNS}
                onRowClick={handleRowClick}
                onRowDoubleClick={handleOpenFiling}
                hideFooterPagination
                hideFooterSelectedRowCount
                getRowClassName={(params) => {
                  const isSelectedRow = params.row.id === selectedNHSId;
                  if (isSelectedRow) return "selected-day-row";
                  return params.indexRelativeToCurrentPage % 2 === 0
                    ? "even"
                    : "odd";
                }}
                slots={{
                  footer: () => (
                    <CountFooter
                      shown={filteredFilings.length}
                      total={filings.length}
                      noun="report"
                    />
                  ),
                }}
                sx={gridRowSx}
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={openFilingDialog}
        onClose={handleCloseFilingDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Filing Details
          <Button
            onClick={handleCloseFilingDialog}
            sx={{ float: "right" }}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {loadingLabReport ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
            </Box>
          ) : lab && Object.keys(lab).length > 0 ? (
            <LabFilingDetails lab={lab} />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SkipFrame;
